import * as THREE from "three";
import Line3d from "../../math/linearAlgebra/line3d";
import RefLineGeometry from "../../model/sceneObject/RefLineGeometry";
import RefLineData from "../../model/sceneObjectData/RefLineData";
import { sceneUnitsFromMm } from "../../utils/scaler";
import { disposeGroup } from "../../utils/disposeUtils";
import LayerMappingsEnum from "../../enums/LayerMappingsEnum";
import { RenderOrderEnum } from "../../enums/RenderOrderEnum";

class RefAxisViewModel {
    data: RefLineData;
    geometry: RefLineGeometry;

    constructor(origin: THREE.Vector3, direction: THREE.Vector3) {
        this.data = new RefLineData(new Line3d(origin, direction));
        this.geometry = new RefLineGeometry();
        this.geometry.group.visible = false; // Hidden until toggleAxesVisibility confirms a snap
    }

    updateGeometry() {
        this.geometry.updateGeometry(this.data);
    }
}

export default class RefAxisHelper {
    #axes: RefAxisViewModel[] = [];
    #group: THREE.Group = new THREE.Group();
    #enabled: boolean = false;

    constructor(origin: THREE.Vector3, axes: THREE.Vector3[]) {
        
        this.#group.layers.set(LayerMappingsEnum.ReferenceLine);
        this.#group.renderOrder = RenderOrderEnum.ReferenceLine;
        this.#group.name = "RefAxisHelper";

        this.setAxes(axes.map(direction => ({ origin, direction })));
        /* this.#axes = axes.map(direction => new RefAxisViewModel(origin, direction));
        this.#axes.forEach(axis => { this.#group.add(axis.geometry.group); }); */
        
    }

    setAxes(refAxes: { origin: THREE.Vector3, direction: THREE.Vector3 }[]) {
        this.#axes.forEach(axis => disposeGroup(axis.geometry.group));
        this.#axes = refAxes.map(a => new RefAxisViewModel(a.origin, a.direction));
        this.#axes.forEach(axis => {
            this.#group.add(axis.geometry.group);
            if (this.#enabled)
                axis.updateGeometry();
        });
    }

    setReferencePoint(point: THREE.Vector3) {
        this.#axes.forEach(axis => {
            axis.data.line.setOrigin(point);
            if (this.#enabled)
                axis.updateGeometry();
        });
    }

    addToScene(scene: THREE.Scene) {
        scene.add(this.#group);
    }

    enable() {
        this.#enabled = true;
        this.#group.visible = true;
    }

    disable() { 
        this.#enabled = false;
        this.#group.visible = false;
    }

    getRefLineAlignedPoint(point: THREE.Vector3, distanceTolerance: number): THREE.Vector3 | null {
        // Prefer snapping to where two ref lines cross, since that's a more specific (harder to hit) target
        // than either line alone.
        let closestCrossing: THREE.Vector3 | null = null;
        let closestCrossingDistSq = distanceTolerance * distanceTolerance;

        for (let i = 0; i < this.#axes.length; i++) {
            for (let j = i + 1; j < this.#axes.length; j++) {
                const crossing = RefAxisHelper.#getLineCrossing(this.#axes[i].data.line, this.#axes[j].data.line, distanceTolerance);
                if (!crossing) continue;

                const distSq = crossing.distanceToSquared(point);
                if (distSq < closestCrossingDistSq) {
                    closestCrossing = crossing;
                    closestCrossingDistSq = distSq;
                }
            }
        }
        if (closestCrossing) return closestCrossing;

        for (const axis of this.#axes) {
            const alignedPoint = axis.data.line.getPointOnLine(point, distanceTolerance);
            if (alignedPoint)
                return alignedPoint;
        }
        return null;
    }

    // Finds where two (possibly skew) 3D lines pass closest to each other, treating them as
    // crossing if that closest distance is within tolerance.
    static #getLineCrossing(lineA: Line3d, lineB: Line3d, tolerance: number): THREE.Vector3 | null {
        const p1 = lineA.getOrigin(new THREE.Vector3());
        const d1 = lineA.getDirection(new THREE.Vector3());
        const p2 = lineB.getOrigin(new THREE.Vector3());
        const d2 = lineB.getDirection(new THREE.Vector3());

        const w0 = p1.clone().sub(p2);
        const a = d1.dot(d1);
        const b = d1.dot(d2);
        const c = d2.dot(d2);
        const d = d1.dot(w0);
        const e = d2.dot(w0);
        const denom = a * c - b * b;

        if (Math.abs(denom) < 1e-9) return null; // Parallel lines never cross

        const sc = (b * e - c * d) / denom;
        const tc = (a * e - b * d) / denom;

        const closest1 = p1.clone().addScaledVector(d1, sc);
        const closest2 = p2.clone().addScaledVector(d2, tc);

        if (closest1.distanceTo(closest2) > tolerance) return null; // Lines pass too far apart to be considered crossing

        return closest1.add(closest2).multiplyScalar(0.5);
    }

    // Shows every axis unconditionally, for callers whose axes are the constraint itself rather
    // than a snap target — there is no "did we snap?" question to answer, so the distance gating
    // in toggleAxesVisibility would just make the line flicker in and out.
    showAllAxes() {
        this.#axes.forEach(a => { a.geometry.group.visible = true; });
    }

    toggleAxesVisibility(point: THREE.Vector3, distanceTolerance: number) {
        this.#axes.forEach(a => { // Light up the axis if point is far enough from origin and on the axis line   
            a.geometry.group.visible = 
            a.data.line.getOrigin(new THREE.Vector3).distanceTo(point) > sceneUnitsFromMm(100) && 
            a.data.line.isOn(point, distanceTolerance);
            
        }); 
    }

    dispose() {
        disposeGroup(this.#group);
    }
    

}